import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getExpirySummary = createAsyncThunk(
  "dashboard/getExpirySummary",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.DASHBOARD.EXPIRY_SUMMARY);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getExpiryDetails = createAsyncThunk(
  "dashboard/getExpiryDetails",
  async (filterType, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.DASHBOARD.EXPIRY_DETAILS}?filterType=${filterType}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getProductivityOverview = createAsyncThunk(
  "dashboard/getProductivityOverview",
  async ({ startDate, endDate, refresh = false } = {}, { rejectWithValue }) => {
    try {
      const url = refresh
        ? `${API_URL.DASHBOARD.PRODUCTIVITY_OVERVIEW}?refresh=true`
        : API_URL.DASHBOARD.PRODUCTIVITY_OVERVIEW;
      const { data } = await axios.post(url, { startDate, endDate });
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const exportProductivityOverview = createAsyncThunk(
  "dashboard/exportProductivityOverview",
  async ({ startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL.DASHBOARD.PRODUCTIVITY_OVERVIEW_EXPORT}?fileType=1`,
        { startDate, endDate },
        { responseType: "blob" }
      );
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename=([^;]+)/);
      const filename = match ? match[1].replace(/"/g, "").trim() : "productivity-overview.csv";
      const blob = new Blob([response.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      return true;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getBelowThreshold = createAsyncThunk(
  "dashboard/getBelowThreshold",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.DASHBOARD.BELOW_THRESHOLD);
      return data?.data || [];
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    expirySummary: null,
    loading: false,
    expiryDetails: [],
    detailsLoading: false,
    productivityRows: [],
    productivityLoading: false,
    belowThreshold: [],
  },
  reducers: {
    resetDashboard(state) {
      state.expirySummary = null;
    },
    resetExpiryDetails(state) {
      state.expiryDetails = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExpirySummary.pending, (state) => {
        state.loading = true;
      })
      .addCase(getExpirySummary.fulfilled, (state, action) => {
        state.expirySummary = action.payload;
        state.loading = false;
      })
      .addCase(getExpirySummary.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getExpiryDetails.pending, (state) => {
        state.detailsLoading = true;
      })
      .addCase(getExpiryDetails.fulfilled, (state, action) => {
        state.expiryDetails = action.payload;
        state.detailsLoading = false;
      })
      .addCase(getExpiryDetails.rejected, (state) => {
        state.detailsLoading = false;
      })
      .addCase(getProductivityOverview.pending, (state) => {
        state.productivityLoading = true;
      })
      .addCase(getProductivityOverview.fulfilled, (state, action) => {
        state.productivityRows = action.payload || [];
        state.productivityLoading = false;
      })
      .addCase(getProductivityOverview.rejected, (state) => {
        state.productivityLoading = false;
      })
      .addCase(getBelowThreshold.fulfilled, (state, action) => {
        state.belowThreshold = action.payload || [];
      });
  },
});

export const { resetDashboard, resetExpiryDetails } = dashboardSlice.actions;
export default dashboardSlice.reducer;
